import keys from 'lodash/fp/keys';
import omitBy from 'lodash/fp/omitBy';
import isUndefined from 'lodash/fp/isUndefined';
import split from 'lodash/fp/split';
import isEmpty from 'lodash/fp/isEmpty';

import { toDistroVersion, toDistroVersions, toSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import { UNKNOWN } from '~/features/base/constants/filterOptions';
import { InstalledDeliverableRange } from '~/features/deliverables/utils/InstalledDeliverableRange';
import { filterEmptyDependencies } from '~/features/deliverables/utils/checkEmptyDeliverableDependecies';

export const stringToBool = string => {
    if (string === 'true') {
        return true;
    }
    if (string === 'false') {
        return false;
    }
    if (typeof string === 'boolean') {
        return string;
    }
    return undefined;
};

const getWithDistroVersion = ({ distroVersion }) => {
    const versions = Array.isArray(distroVersion) ? distroVersion : [distroVersion];
    if (versions.includes('unknown')) {
        return false;
    }
    if (versions.includes('other')) {
        return true;
    }
    return undefined;
};
const getSwVersion = ({ swVersion }) => {
    if (!swVersion) {
        return undefined;
    }
    if (typeof swVersion === 'object' && !Array.isArray(swVersion)) {
        return undefined; // Range is handled separately
    }
    if (Array.isArray(swVersion)) {
        const validVersions = swVersion.filter(v => v && v !== 'unknown' && v !== 'other');
        return validVersions.length > 0 ? validVersions.map(v => toSemanticVersion(v)) : undefined;
    }
    const hasBaseSwVersion = swVersion && swVersion !== 'unknown' && swVersion !== 'other';
    return hasBaseSwVersion ? toSemanticVersion(swVersion) : undefined;
};

const getSwVersionRange = ({ swVersionRange, swVersionMin, swVersionMax }) => {
    const minVersion = swVersionMin || swVersionRange?.minVersion;
    const maxVersion = swVersionMax || swVersionRange?.maxVersion;
    
    if (!minVersion && !maxVersion) {
        return undefined;
    }
    return {
        minValue: minVersion ? toSemanticVersion(minVersion) : undefined,
        maxValue: maxVersion ? toSemanticVersion(maxVersion) : undefined
    };
};
const getDistroVersion = ({ distroVersion }) => {
    if (!distroVersion) {
        return undefined;
    }
    if (Array.isArray(distroVersion)) {
        const validVersions = distroVersion.filter(v => v && v !== 'unknown' && v !== 'other');
        return validVersions.length > 0 ? validVersions.map(v => toDistroVersion(v)) : undefined;
    }
    const hasDistroVersion = distroVersion && distroVersion !== 'unknown' && distroVersion !== 'other';
    return hasDistroVersion ? toDistroVersion(distroVersion) : undefined;
};
const getExcludedDistroVersions = ({ /*distroVersion,*/ excludeDistroVersions }) => {
    // Fixed MANMDM-2189 FE: Device filter: excludedDistroVersions not always sending
    const splittedExcludedDistroVersions = excludeDistroVersions ? split(',', excludeDistroVersions) : undefined;
    return (/*distroVersion === 'other' &&*/ splittedExcludedDistroVersions) ?
        toDistroVersions(splittedExcludedDistroVersions) : undefined;
};
const getSerialNumbers = ({ serialNumbers }) => {
    return serialNumbers ? split(',', serialNumbers) : undefined;
};
const getVINs = ({ vinList }) => {
    return vinList ? split(',', vinList) : undefined;
};
const getShortVINs = ({ shortVinList }) => {
    return shortVinList ? split(',', shortVinList) : undefined;
};
const getWhitelistingInfo = ({ vehicleGroupWhitelist, deviceWhitelist, vehicleGroupBlacklist, deviceBlacklist }) => {
    const splittedVehicleGroupWhitelist = vehicleGroupWhitelist ? split(',', vehicleGroupWhitelist) : undefined;
    const splittedDeviceWhitelist = deviceWhitelist ? split(',', deviceWhitelist) : undefined;
    const splittedVehicleGroupBlacklist = vehicleGroupBlacklist ? split(',', vehicleGroupBlacklist) : undefined;
    const splittedDeviceBlacklist = deviceBlacklist ? split(',', deviceBlacklist) : undefined;

    const containsWhitelist = !!(splittedVehicleGroupWhitelist || splittedDeviceWhitelist);
    const containsBlacklist = !!(splittedVehicleGroupBlacklist || splittedDeviceBlacklist);
    const whitelistedForAll = (containsBlacklist && !containsWhitelist) ? true : undefined;

    const whitelistingInfo = omitBy(isUndefined, {
        whitelistedForAll,
        vehicleGroupWhitelist: splittedVehicleGroupWhitelist,
        deviceWhitelist: splittedDeviceWhitelist,
        vehicleGroupBlacklist: splittedVehicleGroupBlacklist,
        deviceBlacklist: splittedDeviceBlacklist,
    });
    //whitelistingInfo.vehicleGroupWhitelist = ['LATAM @ 26 Nov'];
    //whitelistingInfo.vehicleGroupWhitelist = ['OTA 4'];
    //whitelistingInfo.deviceWhitelist = ['180427-0169', '180427-0168'];
    //whitelistingInfo.vehicleGroupBlacklist = ['LATAM @ 26 Nov'];
    //whitelistingInfo.deviceBlacklist = ['180427-0169', '180427-0168'];
    return keys(whitelistingInfo).length ? whitelistingInfo : undefined;
};

const getVehicleVariantsWhitelistInfo = ({ vehicleVariantsWhitelist }) => {
    //trick to filter only truE variant vehicles
    if (vehicleVariantsWhitelist === true) {
        return ['true'];
    }
    if (Array.isArray(vehicleVariantsWhitelist)) {
        return vehicleVariantsWhitelist;
    } else if (!isEmpty(vehicleVariantsWhitelist)) {
        return [vehicleVariantsWhitelist];
    } else {
        return undefined;
    }
};

const getHardwareVariantList = ({ hwVariantList }) => {
    if (Array.isArray(hwVariantList)) {
        return hwVariantList;
    } else if (!isEmpty(hwVariantList)) {
        return [hwVariantList];
    } else {
        return undefined;
    }
};

const getMileage = ({minMileage, maxMileage}) => {
    if (!minMileage && !maxMileage) {
        return undefined
    }
    return {minValue: +minMileage || undefined, maxValue: +maxMileage || undefined}
}

const getDeviceStatusNames = ({ deviceStatusNames }) => {
    if (Array.isArray(deviceStatusNames)) {
        return deviceStatusNames;
    } else if (!isEmpty(deviceStatusNames)) {
        return [deviceStatusNames];
    } else {
        return undefined;
    }
};


export const filterEmptyInstalledDeliverables = (installedDeliverablesParsed) => {
    filterEmptyDependencies(installedDeliverablesParsed);
    const installedDeliverablesWithVersions = installedDeliverablesParsed?.filter(d => d?.deliverableId !== '' && (d?.versionRange?.minValue || d?.versionRange?.maxValue));
    return installedDeliverablesWithVersions?.length > 0 ? installedDeliverablesWithVersions : undefined;
};

export const getInstalledDeliverables = (installedDeliverables) => {
    let installedDeliverablesAux = installedDeliverables;
    if (installedDeliverablesAux && !Array.isArray(installedDeliverablesAux)) {
        installedDeliverablesAux = [installedDeliverables];
    }
    let installedDeliverablesParsed = installedDeliverablesAux?.map(d => InstalledDeliverableRange.create(d));

    return filterEmptyInstalledDeliverables(installedDeliverablesParsed);
};
export const isSerialNumber = (value) => {
    return Number.parseInt(value[0]) || value[0].toLowerCase() === 'c';
};

export const isShortVin = (value) => {
    return value.length === 7 &&
        /^[A-Za-z0-9]{7}$/.test(value) &&
        !value.includes('-');
};

export const validateShortVINs = (shortVinList) => {
    if (shortVinList.length === 0) {
        return true;
    }
    return shortVinList.every(shortVin => isShortVin(shortVin));
};

export const getLastReportedEssentialImage = (lastEssentialImageReportedVersion) => {
    if (!lastEssentialImageReportedVersion || (Array.isArray(lastEssentialImageReportedVersion) && lastEssentialImageReportedVersion.length === 0)) {
        return undefined;
    }
    if (Array.isArray(lastEssentialImageReportedVersion)) {
        return lastEssentialImageReportedVersion.map(version =>
            version === UNKNOWN ? { version: null } : { version: toSemanticVersion(version) }
        );
    }
    return lastEssentialImageReportedVersion === UNKNOWN
        ? { version: null }
        : { version: toSemanticVersion(lastEssentialImageReportedVersion) };
};

// TODO Think about combining this with filterOptions
export const queryToSearchCriteria = (query) => {
    const {
        serialOrVin, hwVariant, vehicleManufacturerSparePartNumber, deviceType, deviceStatusType,
        testReleasesActive,
        inCustomerFleet,
        updatesActive,
        updateBlockedReason,
        lastCheckForUpdateAfter,
        lastEssentialImageReportedVersion,
        replaced,
        installedDeliverables,
        productionMode,
        ivdMismatch,
        eulaStatus,
        hasMmtProperties,
        vehicleEnergyTypes,
        mmtHeadUnits,
        mmtFunctions,
    } = query;
    return omitBy(isUndefined, {
        serialOrVin,
        hwVariantPrefix: hwVariant,
        hwVariantList: getHardwareVariantList(query),
        vehicleManufacturerSparePartNumber: Array.isArray(vehicleManufacturerSparePartNumber) ? vehicleManufacturerSparePartNumber : (vehicleManufacturerSparePartNumber ? [vehicleManufacturerSparePartNumber] : undefined),
        type: Array.isArray(deviceType) ? deviceType : (deviceType ? [deviceType] : undefined),
        status: Array.isArray(deviceStatusType) ? deviceStatusType : (deviceStatusType ? [deviceStatusType] : undefined),
        checkNames: getDeviceStatusNames(query),
        withDistroVersion: getWithDistroVersion(query),
        softwareVersion: getSwVersion(query),
        softwareVersionRange: getSwVersionRange(query),
        distroVersion: getDistroVersion(query),
        excludedDistroVersions: getExcludedDistroVersions(query),
        serialNumbers: getSerialNumbers(query),
        vinList: getVINs(query),
        shortVinList: getShortVINs(query),
        whitelistingInfo: getWhitelistingInfo(query),
        vehicleVariants: getVehicleVariantsWhitelistInfo(query),
        testReleasesActive: stringToBool(testReleasesActive),
        inCustomerFleet: stringToBool(inCustomerFleet),
        updatesActive: stringToBool(updatesActive),
        updateBlockedReason,
        lastCheckForUpdateAfter,
        mileageRange: getMileage(query),
        installedDeliverables: getInstalledDeliverables(installedDeliverables),
        lastEssentialImageReported: getLastReportedEssentialImage(lastEssentialImageReportedVersion),
        replaced: stringToBool(replaced),
        productionMode: stringToBool(productionMode),
        isIVDMismatch: ivdMismatch === 'all' ? undefined : ivdMismatch,
        eulaStatus: Array.isArray(eulaStatus) ? eulaStatus.map(v => v === 'unknown' ? null : v) : (eulaStatus ? [eulaStatus === 'unknown' ? null : eulaStatus] : undefined),
        hasMmtProperties: stringToBool(hasMmtProperties),
        vehicleEnergyTypes: Array.isArray(vehicleEnergyTypes) ? vehicleEnergyTypes : (vehicleEnergyTypes ? [vehicleEnergyTypes] : undefined),
        mmtHeadUnits: Array.isArray(mmtHeadUnits) ? mmtHeadUnits : (mmtHeadUnits ? [mmtHeadUnits] : undefined),
        mmtFunctions: Array.isArray(mmtFunctions) ? mmtFunctions : (mmtFunctions ? [mmtFunctions] : undefined),
    });
};

